import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { AlertTriangle } from "react-feather";

// Popup de confirmação padrão do site: substitui window.confirm (que quebra
// o visual do site com o dialog nativo do navegador) por um modal no mesmo
// estilo do Toast (ver components/Toast.js). Uso: const ok = await confirmDialog({
//   title, message, confirmLabel, cancelLabel, tone: "danger" | "default"
// }); — retorna uma Promise<boolean> resolvida quando o usuário decide.

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.96) translateY(6px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(10, 17, 30, 0.6);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483100;
  padding: 20px;
  animation: ${fadeIn} 0.15s ease;
`;

const Card = styled.div`
  width: min(420px, 100%);
  background: #1f3050;
  border: 1px solid ${({ $tone }) => ($tone === "danger" ? "rgba(239,68,68,0.28)" : "rgba(34,184,209,0.24)")};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 24px 60px rgba(10, 17, 30, 0.55);
  animation: ${scaleIn} 0.18s ease;
`;

const IconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  background: ${({ $tone }) => ($tone === "danger" ? "rgba(239,68,68,0.12)" : "rgba(34,184,209,0.12)")};
  color: ${({ $tone }) => ($tone === "danger" ? "#f87171" : "#8FE3F2")};
`;

const DialogTitle = styled.h3`
  margin: 0 0 8px;
  color: #f4f7fc;
  font-size: 1.05rem;
  font-weight: 700;
`;

const DialogMessage = styled.p`
  margin: 0 0 22px;
  color: #c7d2e8;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
`;

const BaseButton = styled.button`
  min-height: 42px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  border: 1px solid transparent;
  transition: opacity 0.15s ease, border-color 0.15s ease;

  &:hover {
    opacity: 0.88;
  }
`;

const CancelButton = styled(BaseButton)`
  background: transparent;
  border-color: rgba(148, 163, 184, 0.18);
  color: #c7d2e8;

  &:hover {
    border-color: rgba(148, 163, 184, 0.32);
  }
`;

const ConfirmButton = styled(BaseButton)`
  background: ${({ $tone }) => ($tone === "danger" ? "#EF4444" : "#22B8D1")};
  color: ${({ $tone }) => ($tone === "danger" ? "#FFFFFF" : "#17253D")};
`;

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [request, setRequest] = useState(null);
  const resolverRef = useRef(null);

  const confirmDialog = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setRequest({
        title: options.title || "Confirmar ação",
        message: options.message || "Tem certeza que deseja continuar?",
        confirmLabel: options.confirmLabel || "Confirmar",
        cancelLabel: options.cancelLabel || "Cancelar",
        tone: options.tone || "default",
      });
    });
  }, []);

  const resolveWith = useCallback((value) => {
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
    setRequest(null);
  }, []);

  // Esc cancela, igual a maioria dos modais/dialogs nativos - antes so dava
  // pra fechar clicando fora ou nos botoes.
  useEffect(() => {
    if (!request) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") resolveWith(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [request, resolveWith]);

  return (
    <ConfirmContext.Provider value={{ confirmDialog }}>
      {children}
      {request ? (
        <Overlay role="presentation" onClick={() => resolveWith(false)}>
          <Card
            $tone={request.tone}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <IconWrap $tone={request.tone}>
              <AlertTriangle size={20} />
            </IconWrap>
            <DialogTitle id="confirm-dialog-title">{request.title}</DialogTitle>
            <DialogMessage>{request.message}</DialogMessage>
            <Actions>
              <CancelButton type="button" onClick={() => resolveWith(false)}>
                {request.cancelLabel}
              </CancelButton>
              <ConfirmButton type="button" $tone={request.tone} autoFocus onClick={() => resolveWith(true)}>
                {request.confirmLabel}
              </ConfirmButton>
            </Actions>
          </Card>
        </Overlay>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback caso usado fora do provider (ex.: testes isolados) - evita quebrar a pagina.
    return { confirmDialog: async (options = {}) => window.confirm(options.message || "Confirmar?") };
  }
  return ctx;
}
